import type { ProductSearchResult } from "../../../api/product_catalog"
import type { SalesHierarchyNode } from "../types"
import { HierarchyLevel } from "../types"

/**
 * Fill missing case costs from the product catalog.
 *
 * `list_price` is the only catalog price used: it is per-case, normalized to
 * `units_per_case`. The per-customer `prices` (customer_product_price.unit_price) are
 * deliberately NOT used — their basis varies by import (e.g. Confido exports priced MP
 * 6-packs as the 24-can tray, exactly 4× the sellable case), so seeding from them would
 * silently inflate plans.
 *
 * The fill is metadata-only and fill-only: it sets `case_cost` /
 * `forecast_config.cost_per_case` / per-point `metrics.case_cost` where they are absent,
 * and NEVER rewrites `volume_dollars` — pricing rows that carry volume but no revenue
 * would invent revenue (the same reason importers refuse to price unshipped volume).
 */

export interface CaseCostFill {
  node_id: string
  node_name: string
  cost: number
  matched_by: "product_id" | "upc" | "sku" | "name"
}

export interface CaseCostFillPlan {
  fills: CaseCostFill[]
  /** SKU rows missing a cost that no catalog product could be matched to. */
  unmatched: string[]
  /** SKU rows that already carry a cost somewhere (left untouched). */
  already_priced: number
}

function normalize_key(value: string | undefined | null): string {
  return String(value ?? "").trim().toLowerCase()
}

function node_has_any_case_cost(node: SalesHierarchyNode): boolean {
  if (Number((node as any).case_cost || 0) > 0) return true
  if (Number(node.forecast_config?.cost_per_case || 0) > 0) return true
  return (node.sales_data || []).some(
    (dp: any) => Number(dp?.metrics?.case_cost || 0) > 0
  )
}

interface CatalogLookup {
  by_product_id: Map<string, ProductSearchResult>
  by_identifier: Map<string, ProductSearchResult>
  by_sku: Map<string, ProductSearchResult>
  by_name: Map<string, ProductSearchResult>
}

function build_catalog_lookup(products: ProductSearchResult[]): CatalogLookup {
  const lookup: CatalogLookup = {
    by_product_id: new Map(),
    by_identifier: new Map(),
    by_sku: new Map(),
    by_name: new Map(),
  }
  for (const product of products) {
    if (!(Number(product.list_price || 0) > 0)) continue
    lookup.by_product_id.set(product.id, product)
    for (const identifier of product.identifiers || []) {
      const key = normalize_key(identifier)
      if (key) lookup.by_identifier.set(key, product)
    }
    const sku_key = normalize_key(product.sku)
    if (sku_key) lookup.by_sku.set(sku_key, product)
    const name_key = normalize_key(product.name)
    // Names are the weakest identity — a duplicate name means neither row is safe to price.
    if (name_key) {
      if (lookup.by_name.has(name_key)) {
        lookup.by_name.set(name_key, undefined as unknown as ProductSearchResult)
      } else {
        lookup.by_name.set(name_key, product)
      }
    }
  }
  return lookup
}

function match_sku_node(
  node: SalesHierarchyNode,
  lookup: CatalogLookup
): { product: ProductSearchResult; matched_by: CaseCostFill["matched_by"] } | null {
  const catalog_product_id = (node.metadata as any)?.catalog_product_id
  if (catalog_product_id) {
    const product = lookup.by_product_id.get(String(catalog_product_id))
    if (product) return { product, matched_by: "product_id" }
  }
  const stable = String(node.stable_key ?? "")
  if (stable.startsWith("sku:")) {
    const key = normalize_key(stable.slice("sku:".length))
    const product = lookup.by_identifier.get(key) || lookup.by_sku.get(key)
    if (product) return { product, matched_by: "upc" }
  }
  const sku_key = normalize_key((node as any).product_sku)
  if (sku_key) {
    const product = lookup.by_sku.get(sku_key) || lookup.by_identifier.get(sku_key)
    if (product) return { product, matched_by: "sku" }
  }
  const name_key = normalize_key((node as any).sku_name || node.name)
  if (name_key) {
    const product = lookup.by_name.get(name_key)
    if (product) return { product, matched_by: "name" }
  }
  return null
}

export function plan_case_cost_fill(
  roots: SalesHierarchyNode[],
  products: ProductSearchResult[]
): CaseCostFillPlan {
  const lookup = build_catalog_lookup(products)
  const plan: CaseCostFillPlan = { fills: [], unmatched: [], already_priced: 0 }

  function visit(node: SalesHierarchyNode): void {
    if (String(node.level) === HierarchyLevel.SKU) {
      if (node_has_any_case_cost(node)) {
        plan.already_priced += 1
      } else {
        const match = match_sku_node(node, lookup)
        if (match) {
          plan.fills.push({
            node_id: node.id,
            node_name: node.name,
            // Cents-rounded so the grid's cost inputs never see float dust.
            cost: Math.round(Number(match.product.list_price) * 100) / 100,
            matched_by: match.matched_by,
          })
        } else {
          plan.unmatched.push(node.name)
        }
      }
    }
    for (const child of (node.children ?? []) as SalesHierarchyNode[]) {
      visit(child)
    }
  }

  for (const root of roots) visit(root)
  return plan
}

/**
 * Apply a fill plan: set the cost columns on the planned nodes without repricing.
 * Per-point `metrics.case_cost` is only written where it is currently absent.
 */
export function apply_case_cost_fill(
  roots: SalesHierarchyNode[],
  fills: CaseCostFill[]
): SalesHierarchyNode[] {
  if (fills.length === 0) return roots
  const cost_by_node = new Map(fills.map((f: CaseCostFill) => [f.node_id, f.cost]))

  function visit(node: SalesHierarchyNode): SalesHierarchyNode {
    const children = Array.isArray(node.children)
      ? (node.children as SalesHierarchyNode[]).map(visit)
      : node.children
    const cost = cost_by_node.get(node.id)
    if (cost == null) {
      return children === node.children ? node : ({ ...(node as any), children } as SalesHierarchyNode)
    }
    return {
      ...(node as any),
      children,
      case_cost: cost,
      ...(node.forecast_config
        ? { forecast_config: { ...node.forecast_config, cost_per_case: cost } }
        : {}),
      sales_data: (node.sales_data || []).map((dp: any) =>
        Number(dp?.metrics?.case_cost || 0) > 0
          ? dp
          : {
              ...dp,
              metrics: { ...(dp.metrics || {}), case_cost: cost },
              ...(dp.opportunity_base_metrics && !(Number(dp.opportunity_base_metrics.case_cost || 0) > 0)
                ? { opportunity_base_metrics: { ...dp.opportunity_base_metrics, case_cost: cost } }
                : {}),
            }
      ),
    } as SalesHierarchyNode
  }

  return roots.map(visit)
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function product(overrides: Partial<ProductSearchResult>): ProductSearchResult {
    return {
      id: "prod-1",
      name: "Yuzu 12oz",
      sku: "SZ-001",
      is_pack: false,
      is_sellable: true,
      product_groups: [],
      identifiers: ["840269200397"],
      aliases: [],
      prices: [],
      list_price: 13.56,
      ...overrides,
    } as ProductSearchResult
  }

  function sku_node(overrides: Record<string, unknown>): SalesHierarchyNode {
    return {
      id: "sku-1",
      name: "Yuzu 12oz",
      level: HierarchyLevel.SKU,
      sales_data: [
        {
          period: { year: 2027, month: 1, period_key: "2027-01" },
          metrics: { store_count: 10, cases_sold: 100, volume_dollars: 1000, case_cost: 0 },
          is_forecast: true,
          source_kind: "forecast",
        },
      ],
      ...overrides,
    } as unknown as SalesHierarchyNode
  }

  describe("plan_case_cost_fill", () => {
    it("matches by sku:<upc> stable key and takes the per-case list price", () => {
      const plan = plan_case_cost_fill(
        [sku_node({ stable_key: "sku:840269200397" })],
        [product({})]
      )
      expect(plan.fills).toEqual([
        { node_id: "sku-1", node_name: "Yuzu 12oz", cost: 13.56, matched_by: "upc" },
      ])
      expect(plan.unmatched).toEqual([])
    })

    it("matches by catalog_product_id, product_sku, then name", () => {
      const by_id = plan_case_cost_fill(
        [sku_node({ metadata: { catalog_product_id: "prod-1" }, name: "Something Else" })],
        [product({})]
      )
      expect(by_id.fills[0]?.matched_by).toBe("product_id")

      const by_sku = plan_case_cost_fill(
        [sku_node({ product_sku: "sz-001", name: "Something Else" })],
        [product({})]
      )
      expect(by_sku.fills[0]?.matched_by).toBe("sku")

      const by_name = plan_case_cost_fill([sku_node({})], [product({})])
      expect(by_name.fills[0]?.matched_by).toBe("name")
    })

    it("skips rows that already carry a cost anywhere", () => {
      const priced = sku_node({
        stable_key: "sku:840269200397",
        forecast_config: { cost_per_case: 9, initial_doors: 0, initial_cases_per_month: 0, same_store_sales_multiplier: 0, store_count_growth_multiplier: 0 },
      })
      const plan = plan_case_cost_fill([priced], [product({})])
      expect(plan.fills).toEqual([])
      expect(plan.already_priced).toBe(1)
    })

    it("never prices from a product without a list price, and reports unmatched", () => {
      const plan = plan_case_cost_fill(
        [sku_node({ stable_key: "sku:840269200397" })],
        [product({ list_price: 0 })]
      )
      expect(plan.fills).toEqual([])
      expect(plan.unmatched).toEqual(["Yuzu 12oz"])
    })

    it("refuses a name match when two catalog products share the name", () => {
      const plan = plan_case_cost_fill(
        [sku_node({})],
        [product({}), product({ id: "prod-2", sku: "SZ-002", identifiers: ["840269200999"] })]
      )
      expect(plan.fills).toEqual([])
      expect(plan.unmatched).toEqual(["Yuzu 12oz"])
    })
  })

  describe("apply_case_cost_fill", () => {
    it("sets the cost columns without touching dollars", () => {
      const roots = [sku_node({ stable_key: "sku:840269200397" })]
      const plan = plan_case_cost_fill(roots, [product({})])
      const next = apply_case_cost_fill(roots, plan.fills)
      const node = next[0] as any
      expect(node.case_cost).toBe(13.56)
      expect(node.sales_data[0].metrics.case_cost).toBe(13.56)
      // Pricing must not invent or rescale revenue.
      expect(node.sales_data[0].metrics.volume_dollars).toBe(1000)
    })

    it("is fill-only on per-point costs", () => {
      const roots = [
        sku_node({
          stable_key: "sku:840269200397",
          sales_data: [
            {
              period: { year: 2027, month: 1, period_key: "2027-01" },
              metrics: { store_count: 10, cases_sold: 100, volume_dollars: 1000, case_cost: 0 },
            },
            {
              period: { year: 2027, month: 2, period_key: "2027-02" },
              metrics: { store_count: 10, cases_sold: 100, volume_dollars: 1000, case_cost: 11.25 },
            },
          ],
        }),
      ]
      // The node "has a cost" via Feb, so the planner skips it entirely.
      const plan = plan_case_cost_fill(roots, [product({})])
      expect(plan.already_priced).toBe(1)
      // But a direct apply (hypothetical) must still not overwrite Feb's 11.25.
      const next = apply_case_cost_fill(roots, [
        { node_id: "sku-1", node_name: "Yuzu 12oz", cost: 13.56, matched_by: "upc" },
      ])
      expect((next[0] as any).sales_data[0].metrics.case_cost).toBe(13.56)
      expect((next[0] as any).sales_data[1].metrics.case_cost).toBe(11.25)
    })
  })
}
